import type {
  listCommentPeopleQuerySchema,
  listCommentSiteThreadsQuerySchema,
  resolveMentionablesBodySchema,
} from "schemas/bedrockApi/comments";
import type {
  Comment,
  CommentAnchor,
  CommentMentionPerson,
  CommentSiteThreadPage,
  CommentTarget,
  CommentThread,
  CommentThreadBundle,
  CommentThreadLocation,
  CommitCommentAttachmentResponse,
  ListCommentThreadsQuery,
  MentionableResolution,
  PresignCommentAttachmentResponse,
  UpdateCommentThreadBody,
} from "schemas/generated/comments";
import type { z } from "zod";
import type { RequestFn } from "./request";

import { AgentHTTPClient } from "agent-http-client";
import { exhaustiveSwitch } from "replo-utils/lib/misc";

function threadPath(threadId: string) {
  return `/api/v1/comment-threads/${encodeURIComponent(threadId)}`;
}

function commentPath(commentId: string) {
  return `/api/v1/comments/${encodeURIComponent(commentId)}`;
}

function reactionPath({
  commentId,
  emoji,
}: {
  commentId: string;
  emoji: string;
}) {
  return `${commentPath(commentId)}/reactions/${encodeURIComponent(emoji)}`;
}

/** The people endpoint keys a target as `<kind>:<id>`; a via preview adds its artifact. */
function getPeopleQuery(target: CommentTarget) {
  return exhaustiveSwitch(
    target,
    "kind",
  )<z.input<typeof listCommentPeopleQuerySchema>>({
    file: ({ fileId }) => ({ target: `file:${fileId}` }),
    sitePage: ({ siteId, via }) => ({
      target: `site:${siteId}`,
      viaFileId: via?.fileId,
    }),
    order: ({ orderId }) => ({ target: `order:${orderId}` }),
    asset: ({ assetId }) => ({ target: `asset:${assetId}` }),
  });
}

export function createCommentsClient({ request }: { request: RequestFn }) {
  return {
    listThreads: (input: ListCommentThreadsQuery) => {
      return request<CommentThreadBundle>({
        method: "GET",
        path: "/api/v1/comment-threads",
        query: { ...input },
      });
    },
    listSiteThreads: (
      input: { siteId: string } & z.output<
        typeof listCommentSiteThreadsQuerySchema
      >,
    ) => {
      const { siteId, ...query } = input;
      return request<CommentSiteThreadPage>({
        method: "GET",
        path: `/api/v1/comment-threads/site/${encodeURIComponent(siteId)}`,
        query,
      });
    },
    /** People who can be mentioned in a comment on `target`: name and avatar only. */
    people: async (input: {
      target: CommentTarget;
    }): Promise<CommentMentionPerson[]> => {
      const { people } = await request<{ people: CommentMentionPerson[] }>({
        method: "GET",
        path: "/api/v1/comment-threads/people",
        query: getPeopleQuery(input.target),
      });
      return people;
    },
    createThread: (input: {
      id: string;
      target: CommentTarget;
      anchor: CommentAnchor;
      body: { id: string; body: string };
    }) => {
      return request<{ thread: CommentThread; comment: Comment }>({
        method: "POST",
        path: "/api/v1/comment-threads",
        body: input,
      });
    },
    updateThread: (input: { threadId: string } & UpdateCommentThreadBody) => {
      const { threadId, ...body } = input;
      return request<CommentThread>({
        method: "PATCH",
        path: threadPath(threadId),
        body,
      });
    },
    /** One thread as a bundle, e.g. a permalink to a thread on a deleted page (D-36 §4). */
    getThread: (input: { threadId: string }) => {
      return request<CommentThreadBundle>({
        method: "GET",
        path: threadPath(input.threadId),
      });
    },
    getLocation: (input: { threadId: string }) => {
      return request<CommentThreadLocation>({
        method: "GET",
        path: `${threadPath(input.threadId)}/location`,
      });
    },
    reply: (input: { threadId: string; id: string; body: string }) => {
      const { threadId, ...body } = input;
      return request<Comment>({
        method: "POST",
        path: `${threadPath(threadId)}/comments`,
        body,
      });
    },
    editComment: (input: { commentId: string; body: string }) => {
      return request<Comment>({
        method: "PATCH",
        path: commentPath(input.commentId),
        body: { body: input.body },
      });
    },
    deleteComment: (input: { commentId: string }) => {
      return request<Comment>({
        method: "DELETE",
        path: commentPath(input.commentId),
      });
    },
    putReaction: (input: { commentId: string; emoji: string }) => {
      return request<{ ok: true }>({
        method: "PUT",
        path: reactionPath(input),
      });
    },
    deleteReaction: (input: { commentId: string; emoji: string }) => {
      return request<{ ok: true }>({
        method: "DELETE",
        path: reactionPath(input),
      });
    },
    /** Presign, PUT the bytes straight to storage, then commit; resolves to the URL the body links. */
    uploadAttachment: async (
      file: File,
      target: CommentTarget,
    ): Promise<{ publicUrl: string }> => {
      const contentType = file.type || "application/octet-stream";
      const slot = await request<PresignCommentAttachmentResponse>({
        method: "POST",
        path: "/api/v1/comment-attachments/presign",
        body: { target, contentType, contentLength: file.size },
      });
      const storage = new AgentHTTPClient({
        baseUrl: slot.presignedPutUrl,
        identifier: "Comment attachment storage",
        discardErrorBody: true,
      });
      await storage.putBlob({ path: "", body: file, contentType });
      return request<CommitCommentAttachmentResponse>({
        method: "POST",
        path: "/api/v1/comment-attachments/commit",
        body: { target, objectKey: slot.objectKey },
      });
    },
  };
}

export function createMentionablesClient({ request }: { request: RequestFn }) {
  return {
    /** What the viewer may see of each mention, in input order (D-39). */
    resolve: (input: z.input<typeof resolveMentionablesBodySchema>) => {
      return request<{ results: MentionableResolution[] }>({
        method: "POST",
        path: "/api/v1/mentionables/resolve",
        body: input,
      });
    },
  };
}
